import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import usePageMeta from '../hooks/usePageMeta';

// The page behind the Unsubscribe link, at /unsubscribe/:token. It serves two
// kinds of email:
// - SnowTrack's product email to contractors (server/services/marketingEmail.js),
//   token <userId>.<mac>;
// - a contractor's bulk and season check-in email to their own customers
//   (server/services/customerUnsubscribe.js, migration 112), token
//   c<customerId>.<mac>. The leading c picks the customer route and wording.
// No login: the signed token is the credential. The click on the email link is
// the one click: the page POSTs as soon as it loads and says so. A link scanner
// that fetches this page without running its script unsubscribes nobody, and a
// GET to the API never changes anything. Opening the link again answers the
// same.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export const DONE_TEXT = 'You are unsubscribed from SnowTrack product email.';

// The business's name as the server gave it, or a neutral stand-in when the
// customer record is gone.
const businessName = (name) => (typeof name === 'string' && name.trim()) || 'this business';

export const customerDoneText = (name) => `You will no longer get email from ${businessName(name)}.`;

export const isCustomerToken = (token) => /^c\d/.test(String(token || ''));

function Unsubscribe() {
  const { token } = useParams();
  const customer = isCustomerToken(token);
  const [state, setState] = useState('working');
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState(null);
  // React StrictMode runs effects twice in development; post once.
  const posted = useRef(false);

  usePageMeta({ title: customer ? 'Email preferences' : 'Unsubscribe', robots: 'noindex' });

  useEffect(() => {
    if (posted.current) return;
    posted.current = true;
    const path = isCustomerToken(token) ? 'customers' : 'marketing';
    axios.post(`${API_URL}/api/${path}/unsubscribe/${encodeURIComponent(token)}`)
      .then((res) => {
        setCompanyName(res.data?.companyName || null);
        setState('done');
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 400) setError('This unsubscribe link is not valid. Copy the whole link from the email, or reply to the email to unsubscribe.');
        else if (status === 429) setError('Too many tries. Please wait a while and open the link again.');
        else setError('Something went wrong. Please open the link again, or reply to the email to unsubscribe.');
        setState('error');
      });
  }, [token]);

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">{customer ? 'Email preferences' : 'Product email'}</h1>
        </div>
        {state === 'working' && <p>Unsubscribing...</p>}
        {state === 'done' && !customer && (
          <>
            <p data-testid="unsubscribe-done"><strong>{DONE_TEXT}</strong></p>
            <p>You will still get account email, such as password resets and billing notices.
              You can turn product email back on in Settings, Account.</p>
          </>
        )}
        {state === 'done' && customer && (
          <>
            <p data-testid="unsubscribe-done"><strong>{customerDoneText(companyName)}</strong></p>
            <p>Changed your mind? Contact {businessName(companyName)} and ask them to turn email back on.</p>
          </>
        )}
        {state === 'error' && <p className="form-error" role="alert">{error}</p>}
        <div className="auth-footer">
          {customer
            ? <p data-testid="sent-through">Sent through SnowTrack</p>
            : <p><Link to="/login">Go to SnowTrack</Link></p>}
        </div>
      </div>
    </div>
  );
}

export default Unsubscribe;
